import React, { useEffect, useRef } from "react";

const phases = [
  { step: "1 / 6", name: "Planning", desc: "Orchestrator dispatches full prompt to Sub-agent 1 for task decomposition", tok: "700 tokens", flows: ["f-uo", "f-od"], nodes: ["nn-user", "nn-orch", "nn-deep"], dur: 2800 },
  { step: "2 / 6", name: "Decomposition", desc: "5 sub-tasks returned to orchestrator — ModelRouter assigns optimal model per task", tok: "", flows: ["f-do"], nodes: ["nn-deep", "nn-orch"], dur: 2000 },
  { step: "3 / 6", name: "Host discovery", desc: "GPU hosts discovered on-chain via NodeRegistry — no central directory", tok: "Base Sepolia L2 · smart contract coordination", flows: ["f-or"], nodes: ["nn-orch", "nn-reg"], dur: 1800 },
  { step: "4 / 6", name: "Parallel execution", desc: "Both hosts execute simultaneously — E2E encrypted · STARK proofs generated per inference", tok: "Agent 2: 238+194+355+162 tok  ·  Agent 1: 826+691 tok", flows: ["f-od", "f-of"], nodes: ["nn-orch", "nn-deep", "nn-fast"], dur: 3500 },
  { step: "5 / 6", name: "Result gathering", desc: "Encrypted results return to orchestrator — STARK proofs uploaded to Sia (S5)", tok: "7 inference rounds · 4 blockchain deposits (43% fewer via SessionPool)", flows: ["f-do", "f-fo"], nodes: ["nn-deep", "nn-fast", "nn-orch"], dur: 2200 },
  { step: "6 / 6", name: "On-chain settlement", desc: "TxQueue resolves nonce collision — hosts stateless after settlement, nothing to subpoena", tok: "90% → GPU hosts  ·  10% treasury  ·  unused deposit refunded", flows: ["f-ds", "f-fs"], nodes: ["nn-deep", "nn-fast", "nn-settle"], dur: 2800 },
];

export default function PlatformlessAIAnimation() {
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const curRef = useRef(0);

  useEffect(() => {
    function run(idx) {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const p = phases[idx];

      const pi = wrap.querySelector("#pi");
      pi.style.animation = "none";
      void pi.offsetHeight;
      pi.style.animation = "orch-fadein 0.4s ease";

      wrap.querySelector("#ph-step").textContent = "Phase " + p.step;
      wrap.querySelector("#ph-name").textContent = p.name;
      wrap.querySelector("#ph-desc").textContent = p.desc;
      wrap.querySelector("#ph-tok").textContent = p.tok;

      wrap.querySelectorAll(".fl").forEach((el) => el.classList.remove("on"));
      wrap.querySelectorAll(".nb").forEach((el) => el.classList.remove("on"));
      p.flows.forEach((id) => {
        const e = wrap.querySelector("#" + id);
        if (e) e.classList.add("on");
      });
      p.nodes.forEach((id) => {
        const e = wrap.querySelector("#" + id);
        if (e) e.classList.add("on");
      });
      wrap.querySelectorAll(".pdot").forEach((d, i) => {
        d.classList.toggle("cur", i === idx);
      });

      curRef.current = idx;
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        run((idx + 1) % phases.length);
      }, p.dur);
    }

    run(0);
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <>
      <style>{`
        @keyframes orch-flow-fwd { to { stroke-dashoffset: -30; } }
        @keyframes orch-flow-rev { to { stroke-dashoffset: 30; } }
        @keyframes orch-fadein { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        .fl { opacity: 0; transition: opacity 0.35s; stroke-dasharray: 7 15; }
        .fl.on { opacity: 1; }
        .ff { animation: orch-flow-fwd 0.85s linear infinite; }
        .fr { animation: orch-flow-rev 0.85s linear infinite; }
        .nb { opacity: 0.18; transition: opacity 0.35s; }
        .nb.on { opacity: 1; }
        #pi {
          text-align: center;
          padding: 10px 14px;
          background: rgba(255,255,255,0.04);
          border: 0.5px solid rgba(255,255,255,0.1);
          border-radius: 8px;
          margin: 0 0 8px;
        }
        #ph-step { font-size: 11px; color: #5a5e74; font-family: monospace; letter-spacing: 0.08em; }
        #ph-name { font-size: 14px; font-weight: 500; color: #ececf0; margin: 3px 0 2px; }
        #ph-desc { font-size: 12px; color: #8b8fa4; line-height: 1.5; }
        #ph-tok { font-size: 11px; color: #5a5e74; font-family: monospace; margin-top: 3px; }
        .pdot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #2a2a3e; margin: 0 2px; transition: background 0.3s; }
        .pdot.cur { background: #f59e0b; }
      `}</style>

      <div ref={wrapRef} style={{ borderRadius: 12, padding: "16px 16px 12px", fontFamily: "sans-serif", maxHeight: "100vh", overflow: "hidden" }}>
        <div id="pi">
          <div id="ph-step">Phase 1 / 6</div>
          <div id="ph-name">Planning</div>
          <div id="ph-desc">Loading…</div>
          <div id="ph-tok"></div>
        </div>
        <div id="pdots" style={{ textAlign: "center", marginBottom: 6 }}>
          {phases.map((_, i) => (
            <span key={i} className="pdot" />
          ))}
        </div>

        <svg width="100%" viewBox="0 0 680 502" style={{ display: "block", maxHeight: "70vh" }}>
          <defs>
            <marker id="marr2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </marker>
          </defs>

          {/* Base static lines */}
          <line x1="340" y1="54" x2="340" y2="128" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />
          <line x1="452" y1="157" x2="470" y2="157" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />
          <line x1="286" y1="184" x2="163" y2="276" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />
          <line x1="394" y1="184" x2="517" y2="276" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />
          <line x1="152" y1="346" x2="278" y2="408" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />
          <line x1="528" y1="346" x2="402" y2="408" stroke="#2a2a3e" strokeWidth="0.5" markerEnd="url(#marr2)" />

          {/* Animated flow lines */}
          <line id="f-uo" className="fl ff" x1="340" y1="54" x2="340" y2="128" stroke="#a855f7" strokeWidth="2.5" />
          <line id="f-or" className="fl ff" x1="452" y1="157" x2="470" y2="157" stroke="#3b82f6" strokeWidth="2.5" />
          <line id="f-od" className="fl ff" x1="286" y1="184" x2="163" y2="276" stroke="#f59e0b" strokeWidth="2.5" />
          <line id="f-do" className="fl fr" x1="286" y1="184" x2="163" y2="276" stroke="#f59e0b" strokeWidth="2.5" />
          <line id="f-of" className="fl ff" x1="394" y1="184" x2="517" y2="276" stroke="#22c55e" strokeWidth="2.5" />
          <line id="f-fo" className="fl fr" x1="394" y1="184" x2="517" y2="276" stroke="#22c55e" strokeWidth="2.5" />
          <line id="f-ds" className="fl ff" x1="152" y1="346" x2="278" y2="408" stroke="#4ade80" strokeWidth="2.5" />
          <line id="f-fs" className="fl ff" x1="528" y1="346" x2="402" y2="408" stroke="#4ade80" strokeWidth="2.5" />

          {/* User / Browser */}
          <g id="nn-user" className="nb">
            <rect x="258" y="10" width="164" height="44" rx="8" fill="#10101e" stroke="#3e3e5a" strokeWidth="0.5" />
            <text x="340" y="28" textAnchor="middle" dominantBaseline="central" fill="#ececf0" fontFamily="sans-serif" fontSize="13" fontWeight="500">User / Browser</text>
            <text x="340" y="44" textAnchor="middle" dominantBaseline="central" fill="#5a5e74" fontFamily="sans-serif" fontSize="11">Prompt submitted</text>
          </g>

          {/* Orchestrator */}
          <g id="nn-orch" className="nb">
            <rect x="228" y="128" width="224" height="56" rx="8" fill="#16103a" stroke="#7f77dd" strokeWidth="0.5" />
            <text x="340" y="148" textAnchor="middle" dominantBaseline="central" fill="#cecbf6" fontFamily="sans-serif" fontSize="13" fontWeight="500">Orchestrator</text>
            <text x="340" y="166" textAnchor="middle" dominantBaseline="central" fill="#7f77dd" fontFamily="sans-serif" fontSize="11">Client-side · ModelRouter · SessionPool</text>
          </g>

          {/* NodeRegistry */}
          <g id="nn-reg" className="nb">
            <rect x="470" y="128" width="170" height="56" rx="8" fill="#0c1e38" stroke="#378add" strokeWidth="0.5" />
            <text x="555" y="148" textAnchor="middle" dominantBaseline="central" fill="#b5d4f4" fontFamily="sans-serif" fontSize="13" fontWeight="500">NodeRegistry</text>
            <text x="555" y="166" textAnchor="middle" dominantBaseline="central" fill="#378add" fontFamily="sans-serif" fontSize="11">On-chain · Base Sepolia</text>
          </g>

          {/* Sub-agent 1 */}
          <g id="nn-deep" className="nb">
            <rect x="40" y="276" width="204" height="70" rx="8" fill="#1e1405" stroke="#f59e0b" strokeWidth="0.5" />
            <text x="142" y="293" textAnchor="middle" dominantBaseline="central" fill="#fbbf24" fontFamily="sans-serif" fontSize="13" fontWeight="500">Sub-agent 1</text>
            <text x="142" y="311" textAnchor="middle" dominantBaseline="central" fill="#d97706" fontFamily="sans-serif" fontSize="11">Planning &amp; synthesis</text>
            <text x="142" y="328" textAnchor="middle" dominantBaseline="central" fill="#92400e" fontFamily="monospace" fontSize="10">Jobs #1, #4 · 2217 tokens</text>
          </g>

          {/* Sub-agent 2 */}
          <g id="nn-fast" className="nb">
            <rect x="436" y="276" width="204" height="70" rx="8" fill="#051a10" stroke="#22c55e" strokeWidth="0.5" />
            <text x="538" y="293" textAnchor="middle" dominantBaseline="central" fill="#4ade80" fontFamily="sans-serif" fontSize="13" fontWeight="500">Sub-agent 2</text>
            <text x="538" y="311" textAnchor="middle" dominantBaseline="central" fill="#16a34a" fontFamily="sans-serif" fontSize="11">Fast inference · Q4 quantised</text>
            <text x="538" y="328" textAnchor="middle" dominantBaseline="central" fill="#14532d" fontFamily="monospace" fontSize="10">Jobs #2, #3 · 949 tok · 3 rounds</text>
          </g>

          {/* On-chain Settlement */}
          <g id="nn-settle" className="nb">
            <rect x="240" y="408" width="200" height="70" rx="8" fill="#081a04" stroke="#4ade80" strokeWidth="0.5" />
            <text x="340" y="426" textAnchor="middle" dominantBaseline="central" fill="#86efac" fontFamily="sans-serif" fontSize="13" fontWeight="500">On-chain Settlement</text>
            <text x="340" y="444" textAnchor="middle" dominantBaseline="central" fill="#4ade80" fontFamily="sans-serif" fontSize="11">4 jobs · 0 disputes</text>
            <text x="340" y="461" textAnchor="middle" dominantBaseline="central" fill="#166534" fontFamily="sans-serif" fontSize="11">90% hosts · 10% treasury</text>
          </g>
        </svg>
      </div>
    </>
  );
}
